import { useState } from "react";
import { StudioFeatureModal } from "@/components/shared/StudioFeatureModal";

export type AppTab = "faceswap" | "voice" | "settings";

interface TabBarProps {
  activeTab:       AppTab;
  onTabChange:     (tab: AppTab) => void;
  leftCollapsed:   boolean;
  rightCollapsed:  boolean;
  onToggleLeft:    () => void;
  onToggleRight:   () => void;
  isMobile?:       boolean;
  onClearSession?: () => void;
  onOpenTopUp?:    () => void;
  onOpenProjector?: () => void;
  isProjectorOpen?: boolean;
}

const TABS: { id: AppTab; label: string; icon: string; badge?: string }[] = [
  { id: "faceswap", label: "Face Swap", icon: "◈" },
  { id: "voice",    label: "Voice",     icon: "◎", badge: "Studio" },
  { id: "settings", label: "Settings",  icon: "⊙" },
];


export function TabBar({ activeTab, onTabChange, leftCollapsed, rightCollapsed, onToggleLeft, onToggleRight, isMobile = false, onClearSession, onOpenTopUp, onOpenProjector, isProjectorOpen = false }: TabBarProps) {
  const [showStudioModal, setShowStudioModal] = useState(false);
  return (
    <div style={{
      height: isMobile ? "var(--tab-bar-height)" : "var(--top-bar-height)",
      background: "var(--sidebar-bg)",
      borderBottom: "1px solid var(--border-subtle)",
      display: "flex",
      alignItems: "center",
      padding: "0 var(--space-2)",
      gap: "var(--space-1)",
      flexShrink: 0,
      userSelect: "none",
    }}>
      {/* Logo */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-2)",
        paddingRight: isMobile ? 0 : "var(--space-4)",
        borderRight: isMobile ? "none" : "1px solid var(--border-subtle)",
        marginRight: isMobile ? 0 : "var(--space-2)",
      }}>
        <div style={{
          width: 24, height: 24, borderRadius: "var(--radius-md)",
          background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: "var(--weight-bold)", color: "#000",
        }}>
          S
        </div>
        {!isMobile && (
          <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--text-primary)" }}>
            StreamPersona
          </span>
        )}
      </div>

      {/* Mode tabs */}
      <div style={{ display: "flex", gap: "var(--space-1)", flex: 1, justifyContent: isMobile ? "space-around" : "flex-start" }}>
        {TABS.map(tab => {
          const isStudioOnly = tab.id === "voice";
          return (
            <button
              key={tab.id}
              onClick={() => {
                if (isStudioOnly) {
                  setShowStudioModal(true);
                  return;
                }
                onTabChange(tab.id);
              }}
              title={isStudioOnly ? "Voice Cloning (Studio Workstation Only)" : tab.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? 0 : "var(--space-2)",
                height: isMobile ? 44 : 28,
                padding: isMobile ? "0 var(--space-3)" : "0 var(--space-3)",
                borderRadius: "var(--radius-md)",
                border: "none",
                cursor: isStudioOnly ? "not-allowed" : "pointer",
                opacity: isStudioOnly ? 0.65 : 1,
                fontSize: isMobile ? 16 : "var(--text-sm)",
                fontWeight: activeTab === tab.id ? "var(--weight-semibold)" : "var(--weight-medium)",
                fontFamily: "var(--font-sans)",
                color: activeTab === tab.id ? "var(--text-primary)" : "var(--text-muted)",
                background: activeTab === tab.id ? "var(--bg-elevated)" : "transparent",
                transition: "all var(--duration-fast) var(--ease-out)",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <span style={{ fontSize: isMobile ? 16 : 11 }}>{tab.icon}</span>
              {!isMobile && tab.label}
              {tab.badge && !isMobile && (
                <span style={{
                  fontSize: "9px",
                  padding: "1px 5px",
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  color: "var(--text-secondary)",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Projector / Window Capture Button */}
      {onOpenProjector && (
        <button
          onClick={onOpenProjector}
          title="Open Clean Studio Projector for OBS Window Capture"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-1)",
            height: 28,
            padding: "0 var(--space-3)",
            borderRadius: "var(--radius-md)",
            border: isProjectorOpen ? "1px solid var(--accent)" : "1px solid rgba(255, 255, 255, 0.15)",
            cursor: "pointer",
            fontSize: "var(--text-xs)",
            fontWeight: "var(--weight-semibold)",
            fontFamily: "var(--font-sans)",
            color: isProjectorOpen ? "var(--accent)" : "var(--text-secondary)",
            background: isProjectorOpen ? "rgba(43, 181, 168, 0.15)" : "rgba(255, 255, 255, 0.05)",
            marginRight: "var(--space-2)",
            transition: "all var(--duration-fast) var(--ease-out)",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
          {!isMobile && "Projector"}
          {isProjectorOpen && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--accent)",
                marginLeft: 2,
              }}
            />
          )}
        </button>
      )}

      {/* Top Up Credits Button */}
      {onOpenTopUp && (
        <button
          onClick={onOpenTopUp}
          title="Top Up Streaming Credits"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-1)",
            height: 28,
            padding: "0 var(--space-3)",
            borderRadius: "var(--radius-md)",
            border: "1px solid rgba(0, 212, 170, 0.35)",
            cursor: "pointer",
            fontSize: "var(--text-xs)",
            fontWeight: "var(--weight-semibold)",
            fontFamily: "var(--font-sans)",
            color: "var(--accent)",
            background: "rgba(0, 212, 170, 0.1)",
            marginRight: "var(--space-2)",
            transition: "all var(--duration-fast) var(--ease-out)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(0, 212, 170, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(0, 212, 170, 0.1)";
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          {!isMobile && "Top Up"}
        </button>
      )}

      {/* Clear Session Button */}
      {onClearSession && (
        <button
          onClick={onClearSession}
          title="Clear entire session (wipe uploads, reset profiles, end calls)"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-1)",
            height: 28,
            padding: "0 var(--space-3)",
            borderRadius: "var(--radius-md)",
            border: "1px solid #dc2626",
            cursor: "pointer",
            fontSize: "var(--text-xs)",
            fontWeight: "var(--weight-semibold)",
            fontFamily: "var(--font-sans)",
            color: "#f87171",
            background: "transparent",
            marginRight: "var(--space-2)",
            transition: "all var(--duration-fast) var(--ease-out)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#450a0a";
            e.currentTarget.style.color = "#fca5a5";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#f87171";
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
            <path d="M21 3v5h-5"/>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
            <path d="M3 21v-5h5"/>
          </svg>
          {!isMobile && "Clear Session"}
        </button>
      )}


      {/* Panel toggles — hidden on mobile */}
      {!isMobile && (
        <div style={{ display: "flex", gap: "var(--space-1)" }}>
          <PanelToggle
            label="Left panel"
            icon="⊣"
            active={!leftCollapsed}
            onClick={onToggleLeft}
          />
          <PanelToggle
            label="Right panel"
            icon="⊢"
            active={!rightCollapsed}
            onClick={onToggleRight}
          />
        </div>
      )}

      <StudioFeatureModal
        isOpen={showStudioModal}
        onClose={() => setShowStudioModal(false)}
        featureTitle="Neural Voice & Speech Conversion"
      />
    </div>
  );
}

function PanelToggle({ label, icon, active, onClick }: {
  label: string; icon: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      style={{
        width: 36, height: 36,
        display: "flex", alignItems: "center", justifyContent: "center",
        borderRadius: "var(--radius-md)",
        border: "none",
        cursor: "pointer",
        fontSize: 14,
        color: active ? "var(--text-secondary)" : "var(--text-muted)",
        background: active ? "var(--bg-overlay)" : "transparent",
        transition: "all var(--duration-fast) var(--ease-out)",
      }}
    >
      {icon}
    </button>
  );
}
